/* eslint-disable import/no-cycle */
import {
  useCallback, useEffect, useState, useRef,
} from 'react';
import type {
  Product,
  MeiDou,
  TradeParams,
} from '@meitu/subscribe';
import { getSubscribe } from '$/common/utils/subscribeUtil';
import type { SubscribeInstanceOptions } from '$/common/utils/subscribeUtil';
import { message } from '$/components/Message';
import { useOfficialStore } from '$/store/useOfficialStore';
import { setHasPay } from '$/store/useSubscribeStore';
import useVipModalStore from '$/store/useVipModalStore';
import { checkMeiyeAmount } from '$/api/server';
import { sendBroadcastMessage } from '$/common/utils/broadcastChannel';
import { mtstatTrack } from '$/utils/mtstat';
import { getUrlSearchParams } from '$/utils';
import { useLanguageStore } from '$/store/languageStore';
import { getSourceByPath } from '$/utils/getSourceByPath';
import {
  getVipModalTrackParams,
  getVipModalTrackValue,
} from '$/utils/subscribeTrack';
import type { VipModalTrackParams } from '$/store/useVipModalStore';

type PayType = 'qrcode' | 'stripe';

export interface SubscribePayOptions extends SubscribeInstanceOptions {
  accountId?: TradeParams['accountId'];
  accountType?: TradeParams['accountType'];
  purchaseScene?: TradeParams['purchaseScene'];
  quantity?: TradeParams['quantity'];
}

type SubscribeTradeParams = Pick<
TradeParams,
'accountId' | 'accountType' | 'purchaseScene' | 'quantity'
>;

/**
 * 管理订阅商品支付创建、轮询与订阅埋点上报。
 */
function useSubscriblePay(
  product: Product,
  transferId: string,
  onPaySuccess?: () => void,
  trackParams?: VipModalTrackParams,
) {
  const isOversea = useOfficialStore((state) => state.isOversea());
  const [payType, setPayType] = useState<PayType>(isOversea ? 'stripe' : 'qrcode');
  const openMode = useVipModalStore((state) => (state.openMode));
  const languageStore = useLanguageStore((state) => state.languageStore);
  const userAccountKey = useOfficialStore((state) => `${state.userInfo.uid || ''}:${state.userInfo.token || ''}`);

  const params = getUrlSearchParams();
  const roomId = params.get('room_id') as string || '';
  const fromSource = params.get('from_source') || openMode;
  const intervalIdRef = useRef<any>(null);
  const hasHandledPaySuccessRef = useRef(false);
  const paySdkInstanceRef = useRef<MeiDou | undefined>();
  const paySubscribeOptionsRef = useRef<SubscribeInstanceOptions | undefined>();

  const productRef = useRef(product);
  productRef.current = product;

  const trackParamsRef = useRef(trackParams);
  trackParamsRef.current = trackParams;

  const transferIdRef = useRef(transferId);
  transferIdRef.current = transferId;

  useEffect(() => {
    // 账号切换或支付 hook 卸载时停止旧账号的订单轮询。
    return () => {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    };
  }, [userAccountKey]);

  /**
   * 汇总购买点击埋点参数，允许业务方通过 VipModal 入参补充公共字段。
   */
  const getPayActionTrackParams = useCallback(() => {
    const modalTrackParams = getVipModalTrackParams(trackParamsRef.current);
    return {
      ...modalTrackParams,
      room_id: getVipModalTrackValue(modalTrackParams, 'room_id', roomId),
      from_source: getVipModalTrackValue(modalTrackParams, 'from_source', fromSource),
      position_type: getVipModalTrackValue(modalTrackParams, 'position_type', window.location.pathname),
      product_id: productRef.current?.productId || '',
      sku_name: `${productRef.current?.productName}-${productRef.current.buttonExplain?.price}-${productRef.current?.buttonExplain?.moneySymbol}`,
    };
  }, [fromSource, roomId]);

  /**
   * 汇总支付成功埋点参数，和购买点击保持同一份业务透传。
   */
  const getPaySuccessTrackParams = useCallback(() => {
    const modalTrackParams = getVipModalTrackParams(trackParamsRef.current);
    return {
      ...modalTrackParams,
      room_id: getVipModalTrackValue(modalTrackParams, 'room_id', roomId),
      product_type: 1,
      position_type: getVipModalTrackValue(modalTrackParams, 'position_type', getSourceByPath()),
      from_source: getVipModalTrackValue(modalTrackParams, 'from_source', fromSource),
      product_id: productRef.current?.productId || '',
    };
  }, [fromSource, roomId]);

  /**
   * 商品支付结果轮询，优先复用下单时的 SDK 实例，保证团队上下文一致。
   */
  const startGetTradeResult = () => {
    intervalIdRef.current = setInterval(async () => {
      try {
        const subscribeOptions = paySubscribeOptionsRef.current;
        const sdkInstance = paySdkInstanceRef.current
          || await getSubscribe(subscribeOptions);
        const res = await sdkInstance?.getProductTradeProgress({
          tradeSessionId: productRef.current.tradeSessionId,
        });
        if (res?.havePaidTrade) {
          if (hasHandledPaySuccessRef.current) return;
          hasHandledPaySuccessRef.current = true;
          clearInterval(intervalIdRef.current);
          const paySuccessTrackParams = getPaySuccessTrackParams();
          setHasPay(res.havePaidTrade, `${paySuccessTrackParams.room_id || ''}`);
          sendBroadcastMessage({ type: 'payComplete', params: res });
          message.success(languageStore.smartcatTranslationText((v) => v.recive_top_up));
          mtstatTrack('vip_pay_success', paySuccessTrackParams);
          onPaySuccess?.();
          checkMeiyeAmount();
          setTimeout(() => {
            checkMeiyeAmount();
          }, 8000);
        }
      } catch (error) {
        console.error('轮询请求失败:', error);
        clearInterval(intervalIdRef.current);
      }
    }, 5000);

    return () => clearInterval(intervalIdRef.current);
  };

  /**
   * 使用外部透传的 transferId 创建二维码订单。
   */
  const createTradeQrCode = async (
    sdkInstance: MeiDou,
    tradeParams?: SubscribeTradeParams,
  ) => {
    const { promotions } = productRef.current;
    mtstatTrack('pay_win_click', getPayActionTrackParams());
    const [promotion] = promotions;

    try {
      const response = await sdkInstance?.createTradeQrCode({
        productId: productRef.current.productId,
        transferId: transferIdRef.current,
        promotionId: promotion?.promotionId,
        tradeSessionId: productRef.current.tradeSessionId,
        ...tradeParams,
      });

      setPayType('qrcode');
      startGetTradeResult();
      return response;
    } catch (error) {
      console.error('创建二维码失败:', error);
      const subscribeError = error as any;
      if (subscribeError.errorCode === 'A0001') {
        message.error('A0001');
      }
      throw error;
    }
  };

  /**
   * 创建海外 Stripe 支付链接并开启支付结果轮询。
   */
  const handleStripeLink = async (
    sdkInstance: MeiDou,
    tradeParams?: SubscribeTradeParams,
  ) => {
    const { promotions } = productRef.current;
    mtstatTrack('pay_win_click', getPayActionTrackParams());
    const [promotion] = promotions;

    try {
      const response = await sdkInstance?.createTradeUrl({
        productId: productRef.current.productId,
        transferId: transferIdRef.current,
        promotionId: promotion?.promotionId,
        tradeSessionId: productRef.current.tradeSessionId,
        ...tradeParams,
      });

      if (response.url) {
        startGetTradeResult();
        window.open(response.url);
      }
      setPayType('stripe');
      return response;
    } catch (error) {
      console.error('创建Stripe链接失败:', error);
      throw error;
    }
  };

  if (!product) return { handlePay: null, payResponse: null };

  /**
   * 创建当前商品订单；团队套餐可额外传入席位数量作为交易 quantity。
   */
  const handlePay = async (
    payProduct?: Product,
    payOptions?: SubscribePayOptions,
  ) => {
    if (!transferIdRef.current) {
      console.warn('useSubscriblePay: transferId is empty, skip creating trade');
      return undefined;
    }

    hasHandledPaySuccessRef.current = false;
    if (payProduct) {
      productRef.current = payProduct;
    }
    clearInterval(intervalIdRef.current);

    const {
      oid, groupId, accountId, accountType, purchaseScene, quantity,
    } = payOptions || {};
    const subscribeOptions = { oid, groupId };
    paySubscribeOptionsRef.current = subscribeOptions;

    const sdkInstance = await getSubscribe(subscribeOptions);
    paySdkInstanceRef.current = sdkInstance;

    const tradeParams = {
      accountId,
      accountType,
      purchaseScene,
      quantity,
    };

    if (sdkInstance) {
      if (isOversea) {
        return handleStripeLink(sdkInstance, tradeParams);
      }
      return createTradeQrCode(sdkInstance, tradeParams);
    }
    return undefined;
  };

  const clearIntervalTimer = () => {
    clearInterval(intervalIdRef.current);
    intervalIdRef.current = null;
  };

  return { handlePay, payType, clearIntervalTimer };
}

export default useSubscriblePay;
